import type { ControlFilterBody } from "@/Boards/definition/controls";
import type {
  DerivedStageBody,
  SingleInputStageKind,
} from "@/Boards/definition/stages";

// The words for each kind of stage that reads other stages, so an author
// meets the same words in the panel editor (its Add stage menu and stage
// cards) and in the chat's confirmation cards.

/** A kind of stage that reads other stages: a derived stage, or a Filter by
 *  control stage. */
export type DerivedOrControlFilterKind =
  | DerivedStageBody["kind"]
  | ControlFilterBody["kind"];

/** The name of each kind, as a stage card's header shows it. The Add stage
 *  menu lists "SQL stage", "Combine stages…", and each other kind by its
 *  name alone. */
export const STAGE_KIND_NAMES: Readonly<
  Record<DerivedOrControlFilterKind, string>
> = {
  sql: "SQL",
  combine: "Combine",
  "control-filter": "Filter by control",
  filter: "Filter",
  compute: "Compute",
  unnest: "Unnest",
  limit: "Limit",
  window: "Window",
  group: "Group",
  sort: "Sort",
  resample: "Resample",
  downsample: "Downsample",
};

/** What a stage of each kind that reads one stage does, as a phrase that
 *  starts with its verb: `keep the first rows`. The Add stage menu shows it
 *  as a sentence beside the kind's name. */
export const SINGLE_INPUT_STAGE_PURPOSES: Readonly<
  Record<SingleInputStageKind, string>
> = {
  filter: "keep the rows that satisfy a condition",
  compute: "compute columns from each row, and keep or drop columns",
  unnest: "turn a list column into one row per item",
  limit: "keep the first rows",
  window: "add a row number, a previous value, or a running count or sum",
  group: "aggregate rows, optionally by group",
  sort: "order the rows",
  resample: "put each series on a regular time grid",
  downsample:
    "keep the rows with the lowest and highest values in each interval of a time or number column",
};

/**
 * What a Filter by control stage does, as a phrase that starts with its
 * verb: `keep the rows that match what a viewer chooses in a control`, or,
 * given the stage's column, `keep the rows whose column "name" matches what
 * a viewer chooses in the control "Region"`. `control` names the control.
 */
export function controlFilterPurpose(control: string, column?: string): string {
  const rows =
    column === undefined
      ? "the rows that match"
      : `the rows whose column "${column}" matches`;
  return `keep ${rows} what a viewer chooses in ${control}`;
}
